/**
 * Level 5 — Grand Celebration (unscramble the missing word) + the finale.
 * Answers are NOT stored here; the server checks them (grandCelebration).
 * `scrambled` is the PRD's letter hint shown as tiles.
 */
import deskBg from "./stage5/stage5_desk.png";
import mobBg from "./stage5/stage5_mob.png";

export { deskBg as GRAND_CELEBRATION_DESK_BG, mobBg as GRAND_CELEBRATION_MOB_BG };

export const GRAND_CELEBRATION_PUZZLE_ID = "grand_celebration";

/** Points written to DB `stage5` when every word is solved. */
/* 5 points per correct word solved (8 × 5); the live counter adds them one by one. */
export const GRAND_CELEBRATION_POINTS = 40;

/** Journey completion after the final checkpoint. */
export const GRAND_CELEBRATION_COMPLETION_PERCENT = 100;

/* L&T: words from the ENERGY culture (the server has the same answers for this organization). */
/* L&T: words from the ENERGY culture (the server has the same answers for this organization). */
export const GRAND_CELEBRATION_QUESTIONS = [
  {
    id: "q1",
    before: "When we support each other’s growth, we",
    after: "talent.",
    scrambled: "RUTNREU",
    learning: "Helping others learn and grow builds a stronger team.",
  },
  {
    id: "q2",
    before: "Honest conversations begin with a culture of",
    after: ".",
    scrambled: "SSPENOEN",
    learning: "Sharing ideas freely and listening respectfully creates a safe space for everyone.",
  },
  {
    id: "q3",
    before: "Challenging the familiar takes courage and",
    after: ".",
    scrambled: "SDOBNELS",
    learning: "Asking tough questions and challenging familiar ways opens up new possibilities.",
  },
  {
    id: "q4",
    before: "Taking ownership and raising the bar brings us closer to",
    after: ".",
    scrambled: "LECNEXCEEL",
    learning: "Taking ownership and raising standards helps us deliver our best.",
  },
  {
    id: "q5",
    before: "Trust, opportunity and responsibility lay the foundation for",
    after: ".",
    scrambled: "MEWPOTNEMER",
    learning: "Trusting people with decisions gives them the confidence to take responsibility.",
  },
  {
    id: "q6",
    before: "With coaching and opportunities, we help our",
    after: "shine.",
    scrambled: "NETLAT",
    learning: "Recognising potential and creating learning opportunities helps people shine.",
  },
  {
    id: "q7",
    before: "Curiosity and enthusiasm keep our energy",
    after: ".",
    scrambled: "FUYLOHUT",
    learning: "Bringing curiosity, enthusiasm and fresh thinking keeps our workplace vibrant.",
  },
  {
    id: "q8",
    before: "Fresh ideas come to life when we encourage experimentation and",
    after: ".",
    scrambled: "NOVITANINO",
    learning: "Exploring possibilities and experimenting helps turn fresh ideas into meaningful improvements.",
  },
];

export const GRAND_CELEBRATION_STORY = {
  heading: "The Final Spark Awaits",
  paragraphs: [
    "Every celebration brings us together. Our ENERGY culture helps us grow together.",
    "One final challenge stands between you and the Grand Celebration. Discover the words that inspire how we think, work and support one another.",
  ],
  objective: "Unscramble the letters to complete each sentence. Every correct word lights a lantern!",
  button: "Begin",
};

export const GRAND_CELEBRATION_RETRY = {
  heading: "Not Quite Yet",
  text: "Look closely at the letters and the sentence around the blank. Rearrange the tiles and try again.",
  button: "Try Again",
};

export const GRAND_CELEBRATION_SUCCESS = {
  heading: "You’ve Earned the Final Key!",
  lead: "Eight words uncovered. Eight lanterns glowing!",
  text: "From nurturing talent to embracing innovation, you’ve discovered the spirit of our ENERGY culture.",
  reward: "You have earned the Key of Wisdom.",
  next: "It’s time to unlock the Grand Celebration!",
  button: "Celebrate",
};

export const FINAL_SCREEN = {
  heading: "Thank You!",
  paragraphs: [
    "Thank you for being a part of Offshore IC's Decode Diwali ENERGY. While the game may be complete, the true ENERGY continues when we bring its behaviours to life through our actions, decisions, and interactions every day.",
    "🪔 Keep the ENERGY shining at work and beyond. Happy Diwali!",
  ],
};

/** The five Keys of Knowledge, one per checkpoint. */
export const JOURNEY_KEYS = [
  { id: "traditions", label: "Key of Traditions", checkpoint: "Village Square" },
  { id: "beliefs", label: "Key of Beliefs", checkpoint: "Riverside Crossing" },
  { id: "symbolism", label: "Key of Symbolism", checkpoint: "Festival Market" },
  { id: "values", label: "Key of Values", checkpoint: "Celebration Steps" },
  { id: "wisdom", label: "Key of Wisdom", checkpoint: "Grand Celebration" },
];

const VALID_IDS = new Set(GRAND_CELEBRATION_QUESTIONS.map((q) => q.id));
const CACHE_KEY = "grand_celebration_solved";

export function cleanSolved(list) {
  return Array.isArray(list) ? [...new Set(list.filter((id) => VALID_IDS.has(id)))] : [];
}

/** Solved words from a stages row (`stage5_ans`); empty if none / other puzzle. */
export function readSolvedFromStage(stage) {
  let ans = stage?.stage5_ans;
  if (typeof ans === "string") {
    try {
      ans = JSON.parse(ans);
    } catch {
      ans = null;
    }
  }
  if (!ans || ans.puzzle !== GRAND_CELEBRATION_PUZZLE_ID) return [];
  return cleanSolved(ans.solved);
}

export function readCachedSolved() {
  try {
    return cleanSolved(JSON.parse(sessionStorage.getItem(CACHE_KEY) || "[]"));
  } catch {
    return [];
  }
}

export function writeCachedSolved(solved) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(cleanSolved(solved)));
  } catch {
    /* storage unavailable: DB remains the source of truth */
  }
}

/**
 * Leaderboard submit hook. Stage 8 wires the real submission by listening for
 * `jnb:leaderboard-submit` (detail: { points, time, userId }).
 */
export function requestLeaderboardSubmit(detail) {
  try {
    window.dispatchEvent(new CustomEvent("jnb:leaderboard-submit", { detail }));
  } catch {
    /* no-op outside the browser */
  }
}

async function postJson(backendBase, adminToken, path, body) {
  if (!backendBase || !adminToken) throw new Error("Service unavailable");
  const res = await fetch(`${backendBase}/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) throw new Error(data.message || `${path} failed (${res.status})`);
  return data;
}

/** Server check of an arranged word → { correct }. */
export async function submitGrandCelebrationAnswer({
  backendBase,
  adminToken,
  storedUser,
  stageId,
  questionId,
  answer,
}) {
  const data = await postJson(backendBase, adminToken, "grandCelebrationAnswer", {
    userId: storedUser?.userId || storedUser?.userid || storedUser?.id,
    stageId: stageId || undefined,
    questionId,
    answer,
  });
  return { correct: Boolean(data.correct) };
}

/** Save the post-game feedback label on the player's stages row. */
export async function submitGameFeedback({ backendBase, adminToken, storedUser, feedback }) {
  await postJson(backendBase, adminToken, "welcomeStageFeedback", {
    userId: storedUser?.userId || storedUser?.userid || storedUser?.id,
    feedback,
  });
}

/** One correct letter at `position` → { position, letter }. */
export async function requestGrandCelebrationHint({ backendBase, adminToken, questionId, position }) {
  const data = await postJson(backendBase, adminToken, "grandCelebrationHint", {
    questionId,
    position,
  });
  return { position: data.position, letter: String(data.letter || "") };
}
